import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, FolderOpen, Trash2, Calendar, Tag } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, StatusPill, EmptyState } from "@/admin/components/ui";

const STATUSES = ["all", "draft", "published", "archived"];

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [cat, setCat] = useState("all");
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => ["all", ...Array.from(new Set(events.map((e) => e.category).filter(Boolean)))], [events]);

  const list = events.filter((e) =>
    (status === "all" || e.status === status) &&
    (cat === "all" || e.category === cat) &&
    (!q || e.title.toLowerCase().includes(q.toLowerCase()))
  );

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? This also removes its subfolders.`)) return;
    try {
      await adminApi.delete(`/admin/events/${e.id}`);
      toast.success("Event deleted.");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="page-admin-events" className="space-y-6">
      <SEO title="AdminEvents · WelkomBiz Photography" noindex />
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input
            data-testid="admin-events-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search events…"
            className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body"
          />
        </div>
        <Link to="/admin/events/new" data-testid="admin-events-create" className="btn-primary !py-2.5 !px-5 !text-[11px]">
          <Plus className="w-3.5 h-3.5" /> Create Event
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`admin-events-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border transition-all ${
              status === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto">
          <select
            data-testid="admin-events-category"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-full px-4 py-1.5 text-[10px] tracking-luxe uppercase text-white outline-none"
          >
            {categories.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-white/40">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          testId="admin-events-empty"
          title="No events yet"
          message="Create your first event to start building galleries."
        />
      ) : (
        <Panel className="!p-0 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Event</th>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Category</th>
                  <th className="text-left px-5 py-3">Folders</th>
                  <th className="text-left px-5 py-3">Photos</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-white/3" data-testid={`event-row-${e.id}`}>
                    <td className="px-5 py-4">
                      <p className="text-white font-body">{e.title}</p>
                      <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-0.5">{e.location || "—"}</p>
                    </td>
                    <td className="px-5 py-4 text-white/70">{e.date}</td>
                    <td className="px-5 py-4 text-white/70">{e.category}</td>
                    <td className="px-5 py-4 text-white/70">{e.subfolder_count ?? 0}</td>
                    <td className="px-5 py-4 text-white/70">{e.photo_count ?? 0}</td>
                    <td className="px-5 py-4"><StatusPill status={e.status} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/events/${e.id}/folders`} data-testid={`event-folders-${e.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-white" title="Manage Folders">
                          <FolderOpen className="w-4 h-4" />
                        </Link>
                        <Link to={`/admin/events/${e.id}/pricing`} data-testid={`event-pricing-${e.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-white" title="Event Pricing">
                          <Tag className="w-4 h-4" />
                        </Link>
                        <Link to={`/admin/events/${e.id}/edit`} data-testid={`event-edit-${e.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-white" title="Edit">
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button onClick={() => remove(e)} data-testid={`event-delete-${e.id}`} className="p-2 rounded-md border border-white/10 text-white/70 hover:text-[#ff7fc1]" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-white/5">
            {list.map((e) => (
              <div key={e.id} className="p-4" data-testid={`event-card-${e.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-white font-body min-w-0 truncate">{e.title}</p>
                  <StatusPill status={e.status} />
                </div>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 inline-flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> {e.date} · {e.category}
                </p>
                <p className="text-xs text-white/45 mt-1">{e.subfolder_count ?? 0} folders</p>
                <div className="mt-3 flex items-center gap-2">
                  <Link to={`/admin/events/${e.id}/folders`} className="flex-1 text-center text-[10px] tracking-luxe uppercase py-2 rounded-md border border-white/10 text-white/70">Folders</Link>
                  <Link to={`/admin/events/${e.id}/pricing`} data-testid={`event-pricing-card-${e.id}`} className="flex-1 text-center text-[10px] tracking-luxe uppercase py-2 rounded-md border border-white/10 text-white/70">Pricing</Link>
                  <Link to={`/admin/events/${e.id}/edit`} className="flex-1 text-center text-[10px] tracking-luxe uppercase py-2 rounded-md border border-white/10 text-white/70">Edit</Link>
                  <button onClick={() => remove(e)} className="px-3 py-2 rounded-md border border-white/10 text-[#ff7fc1]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
